/**
 * Uma linha da fila do comercial: quem respondeu, a que toque, ha quanto tempo
 * espera a vendedora (SLA de 15 min), ate quando a venda ainda credita
 * comissao e, se fechou, o desfecho da comissao. As acoes ficam na coluna da
 * direita (empilhadas embaixo no celular).
 *
 * Cor nunca vai sozinha: todo selo tem texto, e os que pedem explicacao levam
 * `title` em termos de negocio (o que o numero quer dizer para o dinheiro).
 */

import { Link } from 'react-router-dom';
import { Badge, Button, Icon, Select, type BadgeTone } from '../../../components/ui';
import { formatBRLFromCents, formatDateTime, maskPhoneDisplay, shortenName } from '../../../lib/format';
import { estaAberto, janelaDoAtendimento, minutosSemResposta, SLA_MINUTOS } from '../fila';
import type { EstadoDaComissao, HandoffItem, HandoffStatus } from '../types';
import { aoContato, prazoCurto } from '../vendaAssistida';

export const STATUS_DO_ATENDIMENTO: Readonly<Record<HandoffStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  aguardando: { label: 'Aguardando vendedora', tone: 'warning' },
  em_atendimento: { label: 'Em atendimento', tone: 'info' },
  fechou: { label: 'Fechou', tone: 'good' },
  perdeu: { label: 'Perdeu', tone: 'neutral' },
};

const COMISSAO: Readonly<Record<EstadoDaComissao, { readonly selo: string; readonly tone: BadgeTone }>> = {
  creditada: { selo: 'Comissão creditada', tone: 'good' },
  revertida: { selo: 'Comissão revertida', tone: 'warning' },
  sem_comissao: { selo: 'Sem comissão', tone: 'neutral' },
};

const DICA_SLA = `SLA: a vendedora responde em até ${SLA_MINUTOS} minutos depois que o lead escreve. Quem espera mais esfria e compra menos.`;
const DICA_JANELA = 'A comissão vale para pagamento em até 7 dias depois do toque que trouxe o lead de volta (ADR-001). É prazo de pagamento, não de registro.';

function SeloDeEspera({ item, agoraMs }: { readonly item: HandoffItem; readonly agoraMs: number }): JSX.Element | null {
  const espera = minutosSemResposta(item, agoraMs);
  if (espera !== null) {
    const tom: BadgeTone = espera > SLA_MINUTOS ? 'critical' : espera > 10 ? 'warning' : 'neutral';
    return (
      <span title={DICA_SLA}>
        <Badge tone={tom} icon="relogio">
          {espera > SLA_MINUTOS ? `sem resposta há ${prazoCurto(espera)} · fora do SLA` : `esperando há ${prazoCurto(espera)}`}
        </Badge>
      </span>
    );
  }
  if (!estaAberto(item) || item.firstResponseMinutes === null) {
    return null;
  }
  const dentro = item.firstResponseMinutes <= SLA_MINUTOS;
  return (
    <span title={DICA_SLA}>
      <Badge tone={dentro ? 'good' : 'warning'} icon="resposta">
        1ª resposta em {prazoCurto(item.firstResponseMinutes)}
        {dentro ? '' : ' · fora do SLA'}
      </Badge>
    </span>
  );
}

export function ItemDaFila({
  item,
  agoraMs,
  vendedoras,
  nomeDaVendedora,
  onAtribuir,
  onRegistrar,
  onPerdeu,
}: {
  readonly item: HandoffItem;
  readonly agoraMs: number;
  readonly vendedoras: readonly { readonly id: string; readonly name: string; readonly abertos: number }[];
  readonly nomeDaVendedora: string | null;
  readonly onAtribuir: (sellerId: string | null) => void;
  readonly onRegistrar: () => void;
  readonly onPerdeu: () => void;
}): JSX.Element {
  const estado = STATUS_DO_ATENDIMENTO[item.status];
  const aberto = estaAberto(item);
  const janela = janelaDoAtendimento(item, agoraMs);
  const nome = shortenName(item.leadName);
  const comissao = item.commissionState === null ? null : COMISSAO[item.commissionState];

  return (
    <li className="flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-ink/[0.03] md:flex-row md:items-start md:gap-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={{ pathname: `/leads/${item.leadId}`, search: window.location.search }} className="font-medium text-ink hover:text-brand hover:underline">
            {nome}
          </Link>
          <span className="text-xs tabular-nums text-ink-subtle">{maskPhoneDisplay(item.phoneE164)}</span>
          <Badge tone={estado.tone} dot>
            {estado.label}
          </Badge>
          <SeloDeEspera item={item} agoraMs={agoraMs} />
        </div>

        <p className="mt-1 text-sm text-ink-muted">
          <span className="text-ink">{item.offerName}</span> · <span className="tabular-nums">{formatBRLFromCents(item.amountCents)}</span> · respondeu ao <strong className="text-ink">{item.touchLabel}</strong>{' '}
          {item.via === 'botao' ? 'pelo botão' : 'digitando'} ·{' '}
          <time dateTime={item.repliedAt} className="tabular-nums">
            {formatDateTime(item.repliedAt)}
          </time>
        </p>

        <p className="mt-1 rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-ink">
          <Icon name="resposta" size="xs" className="mr-1.5 inline text-brand" />“{item.replyText}”
        </p>

        {janela !== null ? (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted" title={DICA_JANELA}>
            <Icon name="escudo" size="xs" className="shrink-0 text-accent" />
            <span>
              Janela de comissão até <span className="tabular-nums text-ink">{formatDateTime(janela.terminaEm)}</span>
            </span>
            <Badge tone={janela.tom}>{janela.rotulo}</Badge>
          </p>
        ) : null}

        {item.status === 'fechou' ? (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
            <span>
              Fechou <strong className="tabular-nums text-ink">{formatBRLFromCents(item.closedAmountCents ?? 0)}</strong> em <span className="tabular-nums">{formatDateTime(item.closedAt)}</span>
              {nomeDaVendedora === null ? '' : ` · fechado por ${nomeDaVendedora}`} · {item.closedVia === 'manual' ? 'venda assistida (registrada à mão)' : 'pagamento pelo webhook'}
              {item.orderRef === null ? '' : ` · pedido ${item.orderRef}`}
            </span>
            {comissao === null ? null : (
              <Badge tone={comissao.tone} dot>
                {comissao.selo}
                {item.commissionState === 'creditada' && item.commissionCents !== null ? ` · ${formatBRLFromCents(item.commissionCents)}` : ''}
              </Badge>
            )}
            {item.commissionState === 'creditada' ? <span>creditada {aoContato(item.touchLabel)}</span> : null}
            {item.commissionState === 'sem_comissao' ? <span>pago fora dos 7 dias do toque</span> : null}
          </p>
        ) : null}

        {item.status === 'perdeu' ? <p className="mt-2 text-xs text-ink-subtle">Encerrado sem venda{nomeDaVendedora === null ? '' : ` · atendido por ${nomeDaVendedora}`}.</p> : null}
      </div>

      <div className="flex w-full shrink-0 flex-col gap-2 md:w-60">
        <Select
          size="sm"
          value={item.sellerId ?? ''}
          onChange={(e) => onAtribuir(e.target.value === '' ? null : e.target.value)}
          aria-label={`Vendedora responsável por ${nome}`}
          disabled={!aberto}
        >
          <option value="">Sem vendedora</option>
          {vendedoras.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} · {s.abertos} {s.abertos === 1 ? 'aberto' : 'abertos'}
            </option>
          ))}
        </Select>
        {aberto ? (
          <div className="flex gap-2">
            <Button size="sm" icon="moeda" onClick={onRegistrar} className="flex-1" aria-label={`Registrar venda de ${nome}`}>
              Registrar venda
            </Button>
            <Button size="sm" variant="ghost" onClick={onPerdeu} aria-label={`Marcar ${nome} como perdido`}>
              Perdeu
            </Button>
          </div>
        ) : (
          <span className="text-xs text-ink-subtle">{item.status === 'fechou' ? 'Encerrado com venda' : 'Encerrado sem venda'}</span>
        )}
      </div>
    </li>
  );
}
